import { useEffect } from "react";
import {
  updateSEO,
  SEOConfig,
  seoByLanguage,
  getCurrentLanguageFromURL,
} from "../lib/SEO/seo.ts";
import { sectionSeoData, SeoSection } from "../lib/Data/seoData.ts";

/**
 * Custom hook for managing SEO in React components
 * Updates document head with SEO meta tags based on current language and page
 */
export const useSEO = (config?: Partial<SEOConfig>, language?: "en" | "zh") => {
  const currentLang = language || getCurrentLanguageFromURL();

  useEffect(() => {
    // Update SEO tags when component mounts or config changes
    updateSEO(config || {}, currentLang);

    // Update html lang attribute
    document.documentElement.lang = currentLang === "zh" ? "zh-TW" : "en";

    // Add hreflang links for language alternatives
    const addHrefLangLinks = () => {
      // Remove existing hreflang links
      const existingLinks = document.querySelectorAll("link[hreflang]");
      existingLinks.forEach((link) => link.remove());

      const currentHash = window.location.hash;

      // English version
      const enLink = document.createElement("link");
      enLink.rel = "alternate";
      enLink.hreflang = "en";
      enLink.href = `https://www.sharonwang.me/${currentHash}`;
      document.head.appendChild(enLink);

      // Chinese version
      const zhLink = document.createElement("link");
      zhLink.rel = "alternate";
      zhLink.hreflang = "zh-TW";
      zhLink.href = `https://www.sharonwang.me/zh${currentHash}`;
      document.head.appendChild(zhLink);

      // x-default (fallback to English)
      const defaultLink = document.createElement("link");
      defaultLink.rel = "alternate";
      defaultLink.hreflang = "x-default";
      defaultLink.href = `https://www.sharonwang.me/${currentHash}`;
      document.head.appendChild(defaultLink);
    };

    addHrefLangLinks();

    // Structured data is defined in index.html (Person, WebSite, ProfilePage, FAQPage)
    // No runtime injection needed — avoids duplicate JSON-LD
  }, [config, currentLang]);

  return {
    updatePageSEO: (newConfig: Partial<SEOConfig>) =>
      updateSEO(newConfig, currentLang),
    currentLanguage: currentLang,
    seoConfig: seoByLanguage[currentLang],
  };
};

/**
 * Hook specifically for section-based SEO updates
 */
export const useSectionSEO = (
  section: SeoSection,
  language?: "en" | "zh"
) => {
  const currentLang = language || getCurrentLanguageFromURL();
  const config = sectionSeoData[section][currentLang];
  useSEO(config, currentLang);
  return config;
};
